import React, { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player/youtube';
import styled from 'styled-components';

import logo from '../../images/ic_logo.svg';
import { useLexicon } from '../../hooks/useLexicon';

export const PLAYER_ONBOARDING_VIDEO_URL =
  'https://youtube.com/shorts/zrtEqdMAU7I';

const YT_PLAYER_CONFIG = {
  youtube: {
    playerVars: {
      modestbranding: 1,
      rel: 0,
      iv_load_policy: 3,
      playsinline: 1,
      controls: 1,
      fs: 0,
      disablekb: 1,
    },
  },
};

const PlayerOnboarding = ({ open, onDismiss, videoUrl = PLAYER_ONBOARDING_VIDEO_URL }) => {
  const l = useLexicon();
  const [ended, setEnded] = useState(false);
  const [playing, setPlaying] = useState(true);
  const playerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setEnded(false);
      setPlaying(true);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const handleEnded = () => {
    setEnded(true);
    setPlaying(false);
  };

  const handleWatchAgain = () => {
    setEnded(false);
    setPlaying(true);
    if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
      playerRef.current.seekTo(0, 'seconds');
    }
  };

  return (
    <Overlay role="dialog" aria-modal="true" aria-label={l(1691)}>
      <Inner>
        <LogoRow>
          <Logo src={logo} alt="SmrtStats" />
        </LogoRow>

        <Heading>{l(1691)}</Heading>
        <Subheading>{l(1692)}</Subheading>

        <VideoFrame>
          <ReactPlayer
            ref={playerRef}
            url={videoUrl}
            playing={playing}
            controls
            width="100%"
            height="100%"
            onEnded={handleEnded}
            config={YT_PLAYER_CONFIG}
          />
        </VideoFrame>

        {ended ? (
          <EndActions>
            <SecondaryButton type="button" onClick={handleWatchAgain}>
              {l(1693)}
            </SecondaryButton>
            <PrimaryButton type="button" onClick={onDismiss}>
              {l(1694)}
            </PrimaryButton>
          </EndActions>
        ) : (
          <SkipRow>
            <SkipButton type="button" onClick={onDismiss}>
              {l(1695)}
            </SkipButton>
          </SkipRow>
        )}
      </Inner>
    </Overlay>
  );
};

export default PlayerOnboarding;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100050;
  background: ${(props) => props.theme.black};
  color: ${(props) => props.theme.white};
  overflow-y: auto;
  display: flex;
  justify-content: center;
`;

const Inner = styled.div`
  width: 100%;
  max-width: 480px;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding:
    calc(env(safe-area-inset-top, 0px) + 20px)
    16px
    calc(env(safe-area-inset-bottom, 0px) + 24px);
  gap: 16px;
`;

const LogoRow = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const Logo = styled.img`
  height: 32px;
  width: auto;
`;

const Heading = styled.h1`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 22px;
  margin: 4px 0 0;
  text-align: center;
`;

const Subheading = styled.p`
  font-family: 'SatoshiRegular', sans-serif;
  font-size: 14px;
  color: ${(props) => props.theme.blueish2};
  margin: 0 0 8px;
  text-align: center;
  max-width: 320px;
`;

const VideoFrame = styled.div`
  width: 100%;
  max-width: 320px;
  aspect-ratio: 9 / 16;
  background: #000;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid ${(props) => props.theme.lines};

  /* react-player wraps the iframe in a div; force it to fill */
  & > div,
  & iframe {
    width: 100% !important;
    height: 100% !important;
  }
`;

const SkipRow = styled.div`
  margin-top: auto;
  padding-top: 12px;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const SkipButton = styled.button`
  background: transparent;
  border: 1px solid ${(props) => props.theme.lines};
  color: ${(props) => props.theme.white};
  padding: 14px 18px;
  border-radius: 10px;
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 14px;
  cursor: pointer;
  width: 100%;
  max-width: 320px;
`;

const EndActions = styled.div`
  margin-top: auto;
  padding-top: 12px;
  width: 100%;
  max-width: 320px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PrimaryButton = styled.button`
  background: ${(props) => props.theme.green};
  border: none;
  color: ${(props) => props.theme.black};
  padding: 16px 18px;
  border-radius: 10px;
  font-family: 'SatoshiBold', sans-serif;
  font-size: 15px;
  cursor: pointer;
  width: 100%;
`;

const SecondaryButton = styled.button`
  background: transparent;
  border: 1px solid ${(props) => props.theme.lines};
  color: ${(props) => props.theme.white};
  padding: 14px 18px;
  border-radius: 10px;
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 14px;
  cursor: pointer;
  width: 100%;
`;
